import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Grid,
  Divider,
  Chip,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import jsPDF from "jspdf";
import logo from "../../assets/Ytc_light.png";
import orderService from "../../repository/orderRepository";

const API = "https://backend.hindustanwardrobe.in";

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pdfCoords, setPdfCoords] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    cardAlt: isDark ? "#151412" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.10)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",
    success: isDark ? "#8FC6A3" : "#3D8054",
    warning: isDark ? "#D7B77A" : "#A66E18",
    danger: isDark ? "#D99494" : "#B54545",
  };

  const cardSx = {
    borderRadius: "14px",
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: isDark
      ? "0 8px 30px rgba(0,0,0,.14)"
      : "0 8px 30px rgba(50,40,30,.045)",
  };

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem("newToken");

      // const response = await axios.get(`${API}/admin/getOrderById`, {
      //   headers: {
      //     "x-access-token": token,
      //     "order-id": id,
      //   },
      // });
      const response = await orderService.getOrderById(id, token);

      setOrder(response.data?.orderData || null);
    } catch (err) {
      console.error("Order detail error:", err);
      setError(err?.response?.data?.message || "Failed to fetch order details");
    } finally {
      setLoading(false);
    }
  };

  const fetchPdfCoordinates = async () => {
    try {
      const token = sessionStorage.getItem("newToken");

      // const response = await axios.get(`${API}/admin/getInvoiceById`, {
      //   headers: {
      //     "layout-id": "ID1731046499022_1272",
      //   },
      // });
      const response = await orderService.getPdfCordinates(token);

      const positions = response.data?.position || [];
      const positionData = positions.reduce((acc, item) => {
        acc[item.componentId] = {
          x: Number(item.x) || 0,
          y: Number(item.y) || 0,
          width: Number(item.width) || 0,
          height: Number(item.height) || 0,
        };
        return acc;
      }, {});

      setPdfCoords(positionData);
    } catch (err) {
      console.error("PDF coordinates error:", err);
      setError("Failed to load invoice layout");
    }
  };

  useEffect(() => {
    fetchOrderDetails();
    fetchPdfCoordinates();
  }, [id]);

  const items = order?.orderItems || [];

  const totalAmount = useMemo(() => {
    return items.reduce((total, item) => {
      return (
        total + (Number(item?.s_Price) || 0) * (Number(item?.quantity) || 0)
      );
    }, 0);
  }, [items]);

  const getStatusConfig = (status) => {
    const value = String(status || "Pending").toLowerCase();

    if (value.includes("complete") || value.includes("deliver")) {
      return {
        label: status || "Completed",
        color: colors.success,
        background: isDark ? "rgba(143,198,163,.10)" : "rgba(61,128,84,.09)",
        icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 15 }} />,
      };
    }

    if (value.includes("cancel")) {
      return {
        label: status || "Canceled",
        color: colors.danger,
        background: isDark ? "rgba(217,148,148,.10)" : "rgba(181,69,69,.08)",
        icon: <CancelOutlinedIcon sx={{ fontSize: 15 }} />,
      };
    }

    return {
      label: status || "Pending",
      color: colors.warning,
      background: isDark ? "rgba(215,183,122,.10)" : "rgba(166,110,24,.08)",
      icon: <PendingActionsOutlinedIcon sx={{ fontSize: 15 }} />,
    };
  };

  const statusConfig = getStatusConfig(order?.status);

  const formatAmount = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN")}`;

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const address = order?.deliveryAddress || {};
  const user = order?.userData || {};

  const handleDownloadPdf = async () => {
    if (!order) return;

    try {
      setPdfLoading(true);

      let coordinates = pdfCoords;

      if (!coordinates) {
        const token = sessionStorage.getItem("newToken");
        // const response = await axios.get(`${API}/admin/getInvoiceById`, {
        //   headers: {
        //     "x-access-token": token,
        //     "layout-id": "ID1731046499022_1272",
        //   },
        // });
        const response = await orderService.getInvoiceById(token);
        coordinates = (response.data?.position || []).reduce((acc, item) => {
          acc[item.componentId] = {
            x: Number(item.x) || 0,
            y: Number(item.y) || 0,
            width: Number(item.width) || 0,
            height: Number(item.height) || 0,
          };
          return acc;
        }, {});

        setPdfCoords(coordinates);
      }

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;

      const safeText = (value) => String(value ?? "-");

      const addWrappedText = (text, x, y, maxWidth, fontSize = 10) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(safeText(text), maxWidth);
        pdf.text(lines, x, y);
        return lines.length * 5;
      };

      pdf.setFillColor(246, 243, 239);
      pdf.rect(0, 0, pageWidth, pageHeight, "F");

      try {
        pdf.addImage(logo, "PNG", margin, 14, 28, 28);
      } catch (e) {}

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.setTextColor(39, 35, 31);
      pdf.text("HINDUSTAN WARDROBE", 50, 22);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(119, 113, 106);
      pdf.text("PREMIUM CLOTHING RENTAL", 50, 28);

      pdf.setDrawColor(128, 107, 82);
      pdf.line(margin, 48, pageWidth - margin, 48);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(16);
      pdf.setTextColor(39, 35, 31);
      pdf.text("INVOICE", margin, 61);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(119, 113, 106);
      pdf.text(`Order ID: ${safeText(order.orderId)}`, pageWidth - margin, 57, {
        align: "right",
      });
      pdf.text(`Date: ${formatDate(order.createdAt)}`, pageWidth - margin, 63, {
        align: "right",
      });

      let currentY = 78;

      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(margin, currentY, pageWidth - margin * 2, 42, 3, 3, "F");

      pdf.setDrawColor(225, 220, 214);
      pdf.roundedRect(margin, currentY, pageWidth - margin * 2, 42, 3, 3, "S");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(128, 107, 82);
      pdf.text("CUSTOMER", margin + 7, currentY + 9);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(39, 35, 31);
      pdf.text(safeText(user.name), margin + 7, currentY + 18);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(119, 113, 106);
      pdf.text(safeText(user.email), margin + 7, currentY + 25);
      pdf.text(`Contact: ${safeText(user.contact)}`, margin + 7, currentY + 32);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(128, 107, 82);
      pdf.text("DELIVERY ADDRESS", 105, currentY + 9);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(119, 113, 106);

      const addressText = [
        address.fullName,
        address.addressLine1,
        address.addressLine2,
        `${address.city || ""}, ${address.state || ""}`,
        `${address.country || ""} - ${address.pinCode || ""}`,
      ]
        .filter(Boolean)
        .join(", ");

      addWrappedText(addressText, 105, currentY + 18, 85, 8);

      currentY += 53;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(39, 35, 31);
      pdf.text("ORDER SUMMARY", margin, currentY);

      currentY += 7;

      const tableX = margin;
      const colWidths = [63, 30, 40, 49];
      const rowHeight = 10;

      pdf.setFillColor(243, 239, 233);
      pdf.rect(tableX, currentY, 182, rowHeight, "F");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(119, 113, 106);

      const headers = ["PRODUCT", "QTY", "PRICE", "TOTAL"];

      let x = tableX + 5;

      headers.forEach((header, index) => {
        pdf.text(header, x, currentY + 6);
        x += colWidths[index];
      });

      currentY += rowHeight;

      pdf.setFont("helvetica", "normal");

      items.forEach((item) => {
        if (currentY > pageHeight - 45) {
          pdf.addPage();
          pdf.setFillColor(246, 243, 239);
          pdf.rect(0, 0, pageWidth, pageHeight, "F");
          currentY = 20;
        }

        const itemTotal =
          (Number(item?.s_Price) || 0) * (Number(item?.quantity) || 0);

        pdf.setDrawColor(225, 220, 214);
        pdf.line(
          tableX,
          currentY + rowHeight,
          tableX + 182,
          currentY + rowHeight,
        );

        pdf.setFontSize(8);
        pdf.setTextColor(39, 35, 31);

        pdf.text(safeText(item?.productId), tableX + 5, currentY + 6);
        pdf.text(safeText(item?.quantity), tableX + 68, currentY + 6);
        pdf.text(formatAmount(item?.s_Price), tableX + 100, currentY + 6);
        pdf.text(formatAmount(itemTotal), tableX + 140, currentY + 6);

        currentY += rowHeight;
      });

      currentY += 8;

      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(105, currentY, 77, 30, 3, 3, "F");

      pdf.setDrawColor(225, 220, 214);
      pdf.roundedRect(105, currentY, 77, 30, 3, 3, "S");

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(119, 113, 106);
      pdf.text("ORDER STATUS", 112, currentY + 9);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(39, 35, 31);
      pdf.text(safeText(order.status), 112, currentY + 17);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(119, 113, 106);
      pdf.text("TOTAL AMOUNT", 112, currentY + 25);

      pdf.setFontSize(13);
      pdf.setTextColor(128, 107, 82);
      pdf.text(formatAmount(totalAmount), 177, currentY + 25, {
        align: "right",
      });

      pdf.setDrawColor(128, 107, 82);
      pdf.line(margin, pageHeight - 25, pageWidth - margin, pageHeight - 25);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(119, 113, 106);
      pdf.text(
        "Thank you for choosing Hindustan Wardrobe.",
        margin,
        pageHeight - 17,
      );
      pdf.text(
        "Generated invoice • Hindustan Wardrobe",
        pageWidth - margin,
        pageHeight - 17,
        { align: "right" },
      );

      pdf.save(`Invoice_Order_${safeText(order.orderId)}.pdf`);
    } catch (err) {
      console.error("PDF generation error:", err);
      setError("Failed to generate invoice");
    } finally {
      setPdfLoading(false);
    }
  };

  const InfoItem = ({ label, value }) => (
    <Box>
      {" "}
      <Typography sx={{ fontSize: 10.5, color: colors.secondary, mb: 0.4 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          color: colors.text,
          fontWeight: 550,
          wordBreak: "break-word",
        }}
      >
        {value || "-"}{" "}
      </Typography>{" "}
    </Box>
  );

  const SectionHeader = ({ icon, title, subtitle }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.3, mb: 2 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.accentSoft,
          color: colors.accent,
          flexShrink: 0,
        }}
      >
        {icon}{" "}
      </Box>{" "}
      <Box>
        {" "}
        <Typography sx={{ fontSize: 15, fontWeight: 650, color: colors.text }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 11, color: colors.secondary, mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}{" "}
      </Box>{" "}
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.background,
        }}
      >
        {" "}
        <CircularProgress size={30} sx={{ color: colors.accent }} />{" "}
      </Box>
    );
  }

  if (!order) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: colors.background,
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper elevation={0} sx={{ ...cardSx, p: 5, textAlign: "center" }}>
          {" "}
          <ReceiptLongOutlinedIcon
            sx={{ fontSize: 40, color: colors.accent, mb: 1 }}
          />{" "}
          <Typography
            sx={{ fontSize: 18, fontWeight: 650, color: colors.text }}
          >
            Order not found
          </Typography>{" "}
          <Typography sx={{ fontSize: 12, color: colors.secondary, mt: 0.6 }}>
            The requested order details could not be loaded.
          </Typography>
          <Button
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackRoundedIcon />}
            sx={{ mt: 2, color: colors.accent, textTransform: "none" }}
          >
            Go Back{" "}
          </Button>{" "}
        </Paper>{" "}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100%",
        backgroundColor: colors.background,
        color: colors.text,
        p: { xs: 1.5, sm: 2.5, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1450, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 1.2, sm: 1.5 },
            mb: 2,
            position: "sticky",
            top: 0,
            zIndex: 5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                width: 40,
                height: 40,
                border: `1px solid ${colors.border}`,
                borderRadius: "9px",
                color: colors.text,
                backgroundColor: colors.cardAlt,
                "&:hover": {
                  backgroundColor: colors.accentSoft,
                  color: colors.accent,
                },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "9px",
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
              }}
            >
              {" "}
              <ShoppingBagOutlinedIcon sx={{ fontSize: 21 }} />{" "}
            </Box>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: 18, sm: 22 },
                  fontWeight: 600,
                  color: colors.text,
                  lineHeight: 1.1,
                }}
              >
                Order Details
              </Typography>
              <Typography
                sx={{
                  fontSize: 10.5,
                  color: colors.secondary,
                  mt: 0.4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                #{order.orderId}
              </Typography>
            </Box>

            <Chip
              icon={statusConfig.icon}
              label={statusConfig.label}
              size="small"
              sx={{
                height: 28,
                borderRadius: "7px",
                fontSize: 10.5,
                fontWeight: 650,
                color: statusConfig.color,
                backgroundColor: statusConfig.background,
                border: `1px solid ${statusConfig.color}22`,
                "& .MuiChip-icon": { color: statusConfig.color },
              }}
            />

            <Tooltip title="Download Invoice">
              <span>
                <Button
                  variant="outlined"
                  startIcon={!isMobile && <PrintRoundedIcon />}
                  onClick={handleDownloadPdf}
                  disabled={pdfLoading}
                  sx={{
                    height: 40,
                    minWidth: { xs: 40, sm: 145 },
                    px: { xs: 1, sm: 2 },
                    borderRadius: "9px",
                    borderColor: colors.border,
                    color: colors.text,
                    backgroundColor: colors.cardAlt,
                    textTransform: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: colors.accent,
                      color: colors.accent,
                      backgroundColor: colors.accentSoft,
                    },
                  }}
                >
                  {pdfLoading ? (
                    <CircularProgress size={17} sx={{ color: colors.accent }} />
                  ) : isMobile ? (
                    <PrintRoundedIcon sx={{ fontSize: 19 }} />
                  ) : (
                    "Download Invoice"
                  )}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Paper>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0,1.55fr) minmax(320px,.7fr)",
            },
            gap: 2,
          }}
        >
          <Box sx={{ display: "grid", gap: 2 }}>
            <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  gap: 2,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: colors.secondary,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      fontWeight: 600,
                    }}
                  >
                    Order Summary
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      fontSize: { xs: 23, sm: 28 },
                      fontWeight: 600,
                      color: colors.text,
                      mt: 0.5,
                    }}
                  >
                    {formatAmount(totalAmount)}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11.5, color: colors.secondary, mt: 0.4 }}
                  >
                    {items.length} {items.length === 1 ? "item" : "items"} •{" "}
                    {formatDate(order.createdAt)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: "9px",
                      backgroundColor: colors.accentSoft,
                    }}
                  >
                    {" "}
                    <Typography sx={{ fontSize: 9.5, color: colors.secondary }}>
                      ORDER ID
                    </Typography>{" "}
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 650,
                        color: colors.text,
                        mt: 0.2,
                      }}
                    >
                      {order.orderId}
                    </Typography>{" "}
                  </Box>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: "9px",
                      backgroundColor: colors.accentSoft,
                    }}
                  >
                    {" "}
                    <Typography sx={{ fontSize: 9.5, color: colors.secondary }}>
                      DATE
                    </Typography>{" "}
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        fontWeight: 650,
                        color: colors.text,
                        mt: 0.2,
                      }}
                    >
                      {formatDate(order.createdAt).split(",")[0]}
                    </Typography>{" "}
                  </Box>{" "}
                </Box>{" "}
              </Box>{" "}
            </Paper>

            <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 } }}>
              <SectionHeader
                icon={<PersonOutlineRoundedIcon sx={{ fontSize: 21 }} />}
                title="Customer Information"
                subtitle="Registered customer details"
              />
              <Divider sx={{ borderColor: colors.border, mb: 2 }} />
              <Grid container spacing={2.2}>
                <Grid item xs={12} sm={6}>
                  <InfoItem label="Full Name" value={user.name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoItem label="Email Address" value={user.email} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoItem label="Contact Number" value={user.contact} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoItem label="Gender" value={user.gender} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoItem label="Date of Birth" value={user.user_DOB} />
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, sm: 2.5 } }}>
              <SectionHeader
                icon={<LocationOnOutlinedIcon sx={{ fontSize: 21 }} />}
                title="Delivery Address"
                subtitle="Shipping destination"
              />
              <Divider sx={{ borderColor: colors.border, mb: 2 }} />
              <Box
                sx={{
                  p: 1.8,
                  borderRadius: "10px",
                  backgroundColor: colors.cardAlt,
                  border: `1px solid ${colors.border}`,
                }}
              >
                {" "}
                <Typography
                  sx={{ fontSize: 13.5, fontWeight: 650, color: colors.text }}
                >
                  {address.fullName || "-"}{" "}
                </Typography>{" "}
                <Typography
                  sx={{
                    fontSize: 12,
                    color: colors.secondary,
                    lineHeight: 1.7,
                    mt: 0.5,
                  }}
                >
                  {[
                    address.addressLine1,
                    address.addressLine2,
                    address.city,
                    address.state,
                    address.country,
                    address.pinCode,
                  ]
                    .filter(Boolean)
                    .join(", ") || "-"}{" "}
                </Typography>{" "}
              </Box>{" "}
            </Paper>

            <Paper elevation={0} sx={{ ...cardSx, p: { xs: 1.5, sm: 2.5 } }}>
              <SectionHeader
                icon={<ShoppingBagOutlinedIcon sx={{ fontSize: 21 }} />}
                title="Order Items"
                subtitle={`${items.length} ${items.length === 1 ? "product" : "products"} in this order`}
              />

              <TableContainer sx={{ overflowX: "auto" }}>
                <Table sx={{ minWidth: 560 }}>
                  <TableHead>
                    <TableRow>
                      {["Product", "Quantity", "Price", "Total"].map(
                        (title) => (
                          <TableCell
                            key={title}
                            sx={{
                              backgroundColor: isDark ? "#1D1A18" : "#F3EFE9",
                              color: colors.secondary,
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "1px",
                              textTransform: "uppercase",
                              borderBottom: `1px solid ${colors.border}`,
                              py: 1.5,
                            }}
                          >
                            {title}
                          </TableCell>
                        ),
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {items.map((item, index) => {
                      const itemTotal =
                        (Number(item?.s_Price) || 0) *
                        (Number(item?.quantity) || 0);

                      return (
                        <TableRow
                          key={`${item?.productId || "product"}-${index}`}
                          sx={{
                            "& td": {
                              borderBottom: `1px solid ${colors.border}`,
                            },
                            "&:hover": { backgroundColor: colors.accentSoft },
                          }}
                        >
                          <TableCell sx={{ py: 1.8 }}>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 650,
                                color: colors.accent,
                                wordBreak: "break-all",
                              }}
                            >
                              {item?.productId || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.8 }}>
                            <Typography
                              sx={{ fontSize: 12, color: colors.text }}
                            >
                              {item?.quantity || 0}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.8 }}>
                            <Typography
                              sx={{ fontSize: 12, color: colors.text }}
                            >
                              {formatAmount(item?.s_Price)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.8 }}>
                            <Typography
                              sx={{
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: colors.text,
                              }}
                            >
                              {formatAmount(itemTotal)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    <TableRow>
                      <TableCell colSpan={3} sx={{ borderBottom: 0, pt: 2 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: colors.secondary,
                            textAlign: "right",
                          }}
                        >
                          Grand Total
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ borderBottom: 0, pt: 2 }}>
                        <Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 700,
                            color: colors.accent,
                          }}
                        >
                          {formatAmount(totalAmount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>

          <Box sx={{ display: "grid", gap: 2, alignSelf: "start" }}>
            <Paper elevation={0} sx={{ ...cardSx, p: 2.3 }}>
              <SectionHeader
                icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 21 }} />}
                title="Order Status"
                subtitle="Current booking status"
              />
              <Box
                sx={{
                  p: 1.7,
                  borderRadius: "10px",
                  backgroundColor: statusConfig.background,
                  border: `1px solid ${statusConfig.color}22`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                }}
              >
                <Box sx={{ color: statusConfig.color, display: "flex" }}>
                  {statusConfig.icon}{" "}
                </Box>{" "}
                <Box>
                  {" "}
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 650,
                      color: statusConfig.color,
                    }}
                  >
                    {statusConfig.label}{" "}
                  </Typography>{" "}
                  <Typography
                    sx={{ fontSize: 10.5, color: colors.secondary, mt: 0.25 }}
                  >
                    Last recorded order status{" "}
                  </Typography>{" "}
                </Box>{" "}
              </Box>{" "}
            </Paper>

            <Paper elevation={0} sx={{ ...cardSx, p: 2.3 }}>
              <SectionHeader
                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 20 }} />}
                title="Order Timeline"
                subtitle="Order activity"
              />
              <Stack spacing={2}>
                <Box sx={{ display: "flex", gap: 1.3 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: colors.accentSoft,
                      color: colors.accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <ReceiptLongOutlinedIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 600, color: colors.text }}
                    >
                      Order Created
                    </Typography>
                    <Typography
                      sx={{ fontSize: 10.5, color: colors.secondary, mt: 0.25 }}
                    >
                      {formatDate(order.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 1.3 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: statusConfig.background,
                      color: statusConfig.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {statusConfig.icon}{" "}
                  </Box>{" "}
                  <Box>
                    {" "}
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 600, color: colors.text }}
                    >
                      Current Status
                    </Typography>{" "}
                    <Typography
                      sx={{ fontSize: 10.5, color: colors.secondary, mt: 0.25 }}
                    >
                      {statusConfig.label}
                    </Typography>{" "}
                  </Box>{" "}
                </Box>
                <Box sx={{ display: "flex", gap: 1.3 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: colors.accentSoft,
                      color: colors.accent,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {" "}
                    <LocalShippingOutlinedIcon sx={{ fontSize: 15 }} />{" "}
                  </Box>{" "}
                  <Box>
                    {" "}
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 600, color: colors.text }}
                    >
                      Delivery Address
                    </Typography>{" "}
                    <Typography
                      sx={{ fontSize: 10.5, color: colors.secondary, mt: 0.25 }}
                    >
                      {address.city || "-"}
                      {address.state ? `, ${address.state}` : ""}{" "}
                    </Typography>{" "}
                  </Box>{" "}
                </Box>{" "}
              </Stack>{" "}
            </Paper>

            <Paper elevation={0} sx={{ ...cardSx, p: 2.3 }}>
              <SectionHeader
                icon={<ShoppingBagOutlinedIcon sx={{ fontSize: 21 }} />}
                title="Payment Summary"
                subtitle="Order amount"
              />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                {" "}
                <Typography sx={{ fontSize: 12, color: colors.secondary }}>
                  Items
                </Typography>{" "}
                <Typography sx={{ fontSize: 12, color: colors.text }}>
                  {items.length}
                </Typography>{" "}
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                {" "}
                <Typography sx={{ fontSize: 12, color: colors.secondary }}>
                  Subtotal
                </Typography>{" "}
                <Typography sx={{ fontSize: 12, color: colors.text }}>
                  {formatAmount(totalAmount)}
                </Typography>{" "}
              </Box>
              <Divider sx={{ my: 1.5, borderColor: colors.border }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {" "}
                <Typography
                  sx={{ fontSize: 12.5, fontWeight: 650, color: colors.text }}
                >
                  Total
                </Typography>{" "}
                <Typography
                  sx={{ fontSize: 20, fontWeight: 700, color: colors.accent }}
                >
                  {formatAmount(totalAmount)}
                </Typography>{" "}
              </Box>{" "}
            </Paper>

            <Paper
              elevation={0}
              sx={{
                ...cardSx,
                p: 2.3,
                backgroundColor: isDark ? "#1D1916" : "#EEE8DF",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: 18,
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                Need the invoice?{" "}
              </Typography>{" "}
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: colors.secondary,
                  lineHeight: 1.5,
                  mt: 0.5,
                }}
              >
                Generate a clean PDF invoice for this order.{" "}
              </Typography>
              <Button
                fullWidth
                onClick={handleDownloadPdf}
                disabled={pdfLoading}
                endIcon={
                  pdfLoading ? (
                    <CircularProgress size={15} sx={{ color: "inherit" }} />
                  ) : (
                    <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                  )
                }
                sx={{
                  mt: 1.7,
                  height: 40,
                  borderRadius: "9px",
                  backgroundColor: colors.text,
                  color: isDark ? "#151412" : "#FFFFFF",
                  textTransform: "none",
                  fontSize: 11.5,
                  fontWeight: 650,
                  "&:hover": {
                    backgroundColor: colors.accent,
                    color: "#FFFFFF",
                  },
                }}
              >
                {pdfLoading ? "Generating..." : "Generate Invoice"}{" "}
              </Button>{" "}
            </Paper>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setError("")}
          sx={{ borderRadius: "10px" }}
        >
          {error}{" "}
        </Alert>{" "}
      </Snackbar>
    </Box>
  );
};

export default OrderDetail;
